/**
 * A plain inline-SVG sparkline — no chart library, colours from the CSS tokens (`.spark-*`).
 *
 * The y axis starts at 0: both series on the Dashboard are counts or durations, and a line
 * scaled min→max would turn a 0.1 ms wobble into a cliff. `null` is a gap (no data in that
 * bucket) and breaks the line; it is never drawn as 0. Every coordinate is guarded so 0 and
 * 1 points render (an empty baseline, a single dot) rather than producing NaN.
 */

const W = 320;
const H = 56;
const PAD = 4;

const fmt = (n: number) => (Math.round(n * 10) / 10).toString();

export function Sparkline({ values, label, testId }: { values: readonly (number | null)[]; label: string; testId?: string }) {
  const n = values.length;
  const finite = values.filter((v): v is number => v !== null && Number.isFinite(v));
  const max = finite.length ? Math.max(0, ...finite) : 0;
  const range = max > 0 ? max : 1;

  const x = (i: number) => (n <= 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (n - 1));
  const y = (v: number) => H - PAD - (Math.max(0, v) / range) * (H - 2 * PAD);

  // Runs of consecutive values; a run of one is a dot, anything longer is a line.
  const runs: { i: number; v: number }[][] = [];
  let run: { i: number; v: number }[] = [];
  values.forEach((v, i) => {
    if (v === null || !Number.isFinite(v)) {
      if (run.length) runs.push(run);
      run = [];
    } else run.push({ i, v });
  });
  if (run.length) runs.push(run);

  const lines = runs.filter((r) => r.length > 1);
  const dots = runs.filter((r) => r.length === 1).map((r) => r[0]!);
  const lastRun = runs[runs.length - 1];
  const end = lastRun?.[lastRun.length - 1];
  if (end && lastRun!.length > 1) dots.push(end);

  return (
    <svg
      className="spark"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      data-testid={testId}
      data-points={n}
    >
      <line className="spark-base" x1={0} y1={H - PAD} x2={W} y2={H - PAD} vectorEffect="non-scaling-stroke" />
      {lines.map((r) => (
        <polyline
          key={r[0]!.i}
          className="spark-line"
          points={r.map((p) => `${fmt(x(p.i))},${fmt(y(p.v))}`).join(' ')}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {/* A zero-length path with a round cap is a dot that stays round under preserveAspectRatio="none". */}
      {dots.map((p) => (
        <path key={`d${p.i}`} className="spark-dot" d={`M${fmt(x(p.i))} ${fmt(y(p.v))}h0`} vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
}
